import {
  cloneElement,
  type CSSProperties,
  isValidElement,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useRef,
} from 'react';

import { useSgTooltipContext } from './SgTooltipContext';

export type SgTooltipTriggerProps = {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  asChild?: boolean;
};

export const SgTooltipTrigger = (props: PropsWithChildren<SgTooltipTriggerProps>) => {
  const { children, className, style, asChild } = props;
  const { refs, setIsOpen, mode, isOpen, setReferenceElement } = useSgTooltipContext();
  const spanRef = useRef<HTMLSpanElement>(null);

  // Gestion des événements selon le mode
  const eventHandlers = (() => {
    switch (mode) {
      case 'hover':
        return {
          onMouseEnter: () => setIsOpen(true),
          onMouseLeave: () => setIsOpen(false),
        };
      case 'click':
        return {
          onClick: () => setIsOpen(!isOpen),
        };
      case 'focus':
        return {
          onFocus: () => setIsOpen(true),
          onBlur: () => setIsOpen(false),
          tabIndex: 0,
        };
      case 'persistent':
      default:
        return {};
    }
  })();

  if (asChild && isValidElement(children)) {
    return cloneElement(children as ReactElement<any>, {
      ref: (el: HTMLElement | null) => {
        refs.setReference(el);
        setReferenceElement(el);

        // Preserve existing ref if any
        const existingRef = (children as any).ref;
        if (typeof existingRef === 'function') {
          existingRef(el);
        } else if (existingRef) {
          existingRef.current = el;
        }
      },

      onMouseEnter: eventHandlers.onMouseEnter
        ? (e: React.MouseEvent) => {
            eventHandlers.onMouseEnter?.();
            (children.props as any).onMouseEnter?.(e);
          }
        : (children.props as any).onMouseEnter,

      onMouseLeave: eventHandlers.onMouseLeave
        ? (e: React.MouseEvent) => {
            eventHandlers.onMouseLeave?.();
            (children.props as any).onMouseLeave?.(e);
          }
        : (children.props as any).onMouseLeave,

      onClick: eventHandlers.onClick
        ? (e: React.MouseEvent) => {
            eventHandlers.onClick?.();
            (children.props as any).onClick?.(e);
          }
        : (children.props as any).onClick,

      onFocus: eventHandlers.onFocus
        ? (e: React.FocusEvent) => {
            eventHandlers.onFocus?.();
            (children.props as any).onFocus?.(e);
          }
        : (children.props as any).onFocus,

      onBlur: eventHandlers.onBlur
        ? (e: React.FocusEvent) => {
            eventHandlers.onBlur?.();
            (children.props as any).onBlur?.(e);
          }
        : (children.props as any).onBlur,

      ...(eventHandlers.tabIndex !== undefined && { tabIndex: eventHandlers.tabIndex }),
    });
  }

  return (
    // oxlint-disable-next-line no-static-element-interactions -- eventHandlers spread below already includes role and keyboard handlers
    <span
      ref={(node) => {
        refs.setReference(node);
        setReferenceElement(node);
        spanRef.current = node;
      }}
      className={className}
      style={style}
      {...eventHandlers}
    >
      {children}
    </span>
  );
};
